import { useCallback, useEffect, useMemo, useState } from 'react'

interface UseMediaDevicesResponse {
  mediaDevices: {
    audioInputs: MediaDeviceInfo[]
    audioOutputs: MediaDeviceInfo[]
  }
  requeryDevices: () => void
}
const useMediaDevices = (): UseMediaDevicesResponse => {
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([])
  const [audioOutputs, setAudioOutputs] = useState<MediaDeviceInfo[]>([])

  const configureAllDevices = useCallback((allDevices: MediaDeviceInfo[]) => {
    if (allDevices.length === 0) return

    const buildList = (kind: MediaDeviceKind) => {
      const available = allDevices.filter((d) => d.kind === kind)
      if (available.length === 0) return []
      const defaultDevice = available[0]
      const rest = available.filter((d) => d.groupId !== defaultDevice.groupId)
      return [defaultDevice, ...rest]
    }

    setAudioInputs(buildList('audioinput'))
    setAudioOutputs(buildList('audiooutput'))
  }, [])

  useEffect(() => {
    let mounted = true

    const onChange = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices()
      if (mounted) {
        configureAllDevices(devices)
      }
    }

    navigator.mediaDevices.addEventListener('devicechange', onChange)
    onChange()

    return () => {
      mounted = false
      navigator.mediaDevices.removeEventListener('devicechange', onChange)
    }
  }, [configureAllDevices])

  const requeryDevices = useCallback(async () => {
    const devices = await navigator.mediaDevices.enumerateDevices()
    configureAllDevices(devices)
  }, [configureAllDevices])

  const mediaDevices = useMemo(
    () => ({ audioOutputs, audioInputs }),
    [audioOutputs, audioInputs],
  )

  return { mediaDevices, requeryDevices }
}

export default useMediaDevices
